import { Component, OnDestroy, OnInit, ElementRef, ViewChild, inject } from '@angular/core';
import { AppViewportScroller } from '../../../../core/services';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';

import { AdminService } from '../../services/admin.service';
import { isPhoneViewport } from '../../../../core/utils/phone';
import { ModalService } from '../../../../shared/modal';
import { AdminFilterField, countActiveFilters, openAdminFilters } from '../../filters-sheet/admin-filters-sheet';
import { AdminOrganizationRow, AdminOrganizationTotals } from '../../models/admin-organization.model';
import { AdminPagination } from '../../models/admin-pagination.model';
import { filterLabel } from '../../utils/filter-label';

const PER_PAGE = 50;

/** Filters live in the URL (?q=&exempt=true&focus=true&page=) per the
 *  contract's "Screens" note, so a link to exactly this view is shareable.
 *  Any filter/page change re-syncs the URL (replaceUrl: true, so typing in
 *  the search box doesn't spam browser history) and re-fetches. */
@Component({
  selector: 'app-admin-organizations',
  standalone: false,
  templateUrl: './admin-organizations.html',
  styleUrl: './admin-organizations.scss',
})
export class AdminOrganizations implements OnInit, OnDestroy {
  private readonly _destroy$ = new Subject<void>();
  private readonly reload$ = new Subject<void>();

  rows: AdminOrganizationRow[] = [];
  /** For the whole filtered list; null until the backend sends `totals`
   *  (the Totals card is hidden until then). */
  totals: AdminOrganizationTotals | null = null;
  pagination: AdminPagination | null = null;
  isLoading = false;
  /** Until the first response: the Totals card shows a skeleton meanwhile. */
  hasLoadedOnce = false;

  search = '';
  exemptOnly = false;
  focusOnly = false;
  page = 1;

  /** Phones get a Filters button and sheet instead of the inline filters. */
  readonly phone = isPhoneViewport();
  private readonly modals = inject(ModalService);

  private readonly filterFields: AdminFilterField[] = [
    { kind: 'segments', key: 'billing', label: 'Billing', options: [{ value: 'all', label: 'All billing' }, { value: 'exempt', label: 'Exempt only' }] },
    { kind: 'segments', key: 'focus', label: 'Organizations', options: [{ value: 'all', label: 'All' }, { value: 'focus', label: 'FOCUS only' }] },
  ];
  private readonly filterDefaults = { billing: 'all', focus: 'all' };

  private get filterValues(): Record<string, string> {
    return { billing: this.exemptOnly ? 'exempt' : 'all', focus: this.focusOnly ? 'focus' : 'all' };
  }

  /** Phones: every filter but the search, in a sheet; applied in one go. */
  openFilters(): void {
    openAdminFilters(this.modals, { fields: this.filterFields, values: this.filterValues, defaults: this.filterDefaults }, v => {
      this.exemptOnly = v['billing'] === 'exempt';
      this.focusOnly = v['focus'] === 'focus';
      this.applyFilters();
    });
  }

  /** Badge on the phone Filters button: how many filters are set. */
  get activeFilterCount(): number {
    return countActiveFilters(this.filterValues, this.filterDefaults);
  }

  constructor(
    private adminService: AdminService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    const params = this.route.snapshot.queryParamMap;
    this.search = params.get('q') ?? '';
    this.exemptOnly = params.get('exempt') === 'true';
    this.focusOnly = params.get('focus') === 'true';
    this.page = Number(params.get('page')) || 1;

    this.reload$.pipe(
      debounceTime(250),
      switchMap(() => {
        this.isLoading = true;
        return this.adminService.getOrganizations({
          q: this.search.trim() || undefined,
          exempt: this.exemptOnly || undefined,
          focus: this.focusOnly || undefined,
          page: this.page,
          perPage: PER_PAGE,
        });
      }),
      takeUntil(this._destroy$),
    ).subscribe(({ rows, totals, pagination }) => {
      this.rows = rows;
      this.totals = totals;
      this.pagination = pagination;
      this.isLoading = false;
      this.hasLoadedOnce = true;
    });

    this.reload$.next();
  }

  /** Any filter change resets to page 1 new criteria, old page number
   *  would likely be past the end. */
  private applyFilters(): void {
    this.page = 1;
    this.syncUrl();
  }

  onSearchChange(value: string): void {
    this.search = value;
    this.applyFilters();
  }

  onExemptChange(value: string): void {
    this.exemptOnly = value === 'exempt';
    this.applyFilters();
  }

  onFocusChange(value: string): void {
    this.focusOnly = value === 'focus';
    this.applyFilters();
  }

  @ViewChild('listCard') private listCard?: ElementRef<HTMLElement>;
  private readonly scroller = inject(AppViewportScroller);

  goToPage(page: number): void {
    if (page < 1 || (this.pagination && page > this.pagination.pages)) return;
    this.page = page;
    // Next/Previous land on the top of the list, not the top of the page.
    this.scroller.overrideNextScroll(() => this.listCard?.nativeElement.scrollIntoView({ block: 'start' }));
    this.syncUrl();
  }

  clearFilters(): void {
    this.search = '';
    this.exemptOnly = false;
    this.focusOnly = false;
    this.applyFilters();
  }

  get hasFilters(): boolean {
    return !!this.search || this.exemptOnly || this.focusOnly;
  }

  get totalsFilterLabel(): string | null {
    return filterLabel([
      this.search.trim() && `“${this.search.trim()}”`,
      this.exemptOnly && 'exempt only',
      this.focusOnly && 'FOCUS only',
    ]);
  }

  private syncUrl(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        q: this.search.trim() || null,
        exempt: this.exemptOnly ? 'true' : null,
        focus: this.focusOnly ? 'true' : null,
        page: this.page > 1 ? this.page : null,
      },
      replaceUrl: true,
    });
    this.reload$.next();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
